import { http, HttpResponse, passthrough, type PathParams, type HttpHandler } from 'msw';

import {
  createMockTripSearchResponse,
  createMockTripSearchParameters,
  createMockAvailabilityGroups,
  createMockQuotationResponse,
  createMockBookingResponse,
  createMockBookingsListResponse,
  createMockAvailabilityBrowseResponse,
  createMockPassengersResponse,
  createMockPassengerEvaluationResponse,
} from '../factories/bookings';
import { DELAYS, delay } from '../utils/network';
import type { TripSearchPayload } from '../../api/models/bff/TripSearch';
import type { QuotationPayload } from '../../api/models/bff/Quotation';
import type { CreateBookingPayload } from '../../api/models/bff/Booking';
import type { PassengerEvaluationPayload } from '../../api/models/bff/Passenger';
import { BFF_ENDPOINTS } from '../../api/endpoints/BffEndpoints';
import { toMSWPattern } from '../utils/generic';
import type { HTTP_METHODS } from './constants/generic';
import { HTTP_METHODS_VALUES } from './constants/generic';

interface HandlerMapping {
  pattern: string;
  key: string;
  method?: HTTP_METHODS;
}

interface HandlerWithMapping {
  handler: HttpHandler;
  mapping: HandlerMapping;
}

export const ENDPOINTS_KEYS = {
  TRIP_SEARCH: 'TRIP_SEARCH',
  AVAILABILITY_BROWSE: 'AVAILABILITY_BROWSE',
  TRIP_SEARCH_PARAMS: 'TRIP_SEARCH_PARAMS',
  AVAILABILITY_GROUPS: 'AVAILABILITY_GROUPS',
  QUOTATION: 'QUOTATION',
  LIST_BOOKINGS: 'LIST_BOOKINGS',
  BOOKING: 'BOOKING',
  CREATE_BOOKING: 'CREATE_BOOKING',
  BOOKING_PAYMENTS: 'BOOKING_PAYMENTS',
  BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS: 'BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS',
  SUBSCRIPTION_PASSENGERS: 'SUBSCRIPTION_PASSENGERS',
  SUBSCRIPTION_PASSENGERS_EVALUATION: 'SUBSCRIPTION_PASSENGERS_EVALUATION',
};

/**
 * MSW handlers for booking endpoints
 */

function tripSearchHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.post<PathParams, TripSearchPayload>(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.TRIP_SEARCH)}`,
      async ({ request, params }: { request: Request; params: PathParams }) => {
        const body = await request.json();

        console.log('[MSW] Trip search request:', {
          subscriptionId: params.subscriptionId,
          body,
        });

        await delay(DELAYS.MEDIUM);

        const response = createMockTripSearchResponse();
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'availability',
      key: ENDPOINTS_KEYS.TRIP_SEARCH,
      method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
    },
  };
}

function availabilityBrowseHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.get(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.TRIP_SEARCH)}`,
      async ({ request, params }: { request: Request; params: PathParams }) => {
        const url = new URL(request.url);
        const origin = url.searchParams.get('origin') ?? 'MAD';
        const destination = url.searchParams.get('destination') ?? 'BCN';
        const from = url.searchParams.get('from');
        const to = url.searchParams.get('to');

        console.log('[MSW] Availability browse request:', {
          subscriptionId: params.subscriptionId,
          origin,
          destination,
          from,
          to,
        });

        await delay(DELAYS.MEDIUM);

        const dates = enumerateDates(from, to);
        const response = createMockAvailabilityBrowseResponse(origin, destination, dates);
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'availability',
      key: ENDPOINTS_KEYS.AVAILABILITY_BROWSE,
      method: HTTP_METHODS_VALUES.GET as HTTP_METHODS,
    },
  };
}

/** Builds an inclusive list of ISO dates (YYYY-MM-DD) between `from` and `to`. */
function enumerateDates(from: string | null, to: string | null): string[] {
  if (!from || !to) return from ? [from] : [];

  const dates: string[] = [];
  const current = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (current <= end) {
    dates.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return dates;
}

function tripSearchParamsHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.get(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.TRIP_SEARCH_PARAMS)}`,
      async ({ params }: { params: PathParams }) => {
        console.log('[MSW] Trip search parameters request:', {
          subscriptionId: params.subscriptionId,
        });

        await delay(DELAYS.SHORT);

        const response = createMockTripSearchParameters();
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'availability-params',
      key: ENDPOINTS_KEYS.TRIP_SEARCH_PARAMS,
    },
  };
}

function availabilityGroupsHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.get(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.AVAILABILITY_GROUPS)}`,
      async ({ params }: { params: PathParams }) => {
        console.log('[MSW] Availability groups request:', {
          subscriptionId: params.subscriptionId,
        });

        await delay(DELAYS.SHORT);

        const response = createMockAvailabilityGroups();
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'availability-params/groups',
      key: ENDPOINTS_KEYS.AVAILABILITY_GROUPS,
    },
  };
}

function quotationHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.post<PathParams, QuotationPayload>(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.QUOTATION)}`,
      async ({ request, params }: { request: Request; params: PathParams }) => {
        const body = await request.json();

        console.log('[MSW] Quotation request:', {
          subscriptionId: params.subscriptionId,
          body,
        });

        await delay(DELAYS.MEDIUM);

        const response = createMockQuotationResponse();
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'quotation',
      key: ENDPOINTS_KEYS.QUOTATION,
      method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
    },
  };
}

function listBookingsHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.get(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING_LIST)}`,
      async ({ request, params }: { request: Request; params: PathParams }) => {
        const url = new URL(request.url);
        const page = Number(url.searchParams.get('page')) || 1;

        console.log('[MSW] List bookings request:', {
          subscriptionId: params.subscriptionId,
          page,
        });

        await delay(DELAYS.MEDIUM);

        const response = createMockBookingsListResponse({ page });
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'bookings',
      key: ENDPOINTS_KEYS.LIST_BOOKINGS,
    },
  };
}

function getBookingHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.get(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING)}`,
      async ({ params }: { params: PathParams }) => {
        if (params.bookingId === 'availability-params') {
          return passthrough();
        }
        console.log('[MSW] Get booking request:', {
          subscriptionId: params.subscriptionId,
          bookingId: params.bookingId,
        });

        await delay(DELAYS.SHORT);

        const response = createMockBookingResponse({
          id: params.bookingId as string,
        });
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'bookings/:bookingId',
      key: ENDPOINTS_KEYS.BOOKING,
    },
  };
}

function createBookingHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.post<PathParams, CreateBookingPayload>(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.CREATE_BOOKING)}`,
      async ({ request, params }: { request: Request; params: PathParams }) => {
        const body = await request.json();

        console.log('[MSW] Create booking request:', {
          subscriptionId: params.subscriptionId,
          body,
        });

        await delay(DELAYS.LONG);

        const response = createMockBookingResponse({
          bookingStatus: 'DRAFT',
          paymentStatus: 'PENDING',
        });
        return HttpResponse.json(response, { status: 201 });
      },
    ),
    mapping: {
      pattern: 'bookings',
      key: ENDPOINTS_KEYS.CREATE_BOOKING,
      method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
    },
  };
}

// Booking payment: a tokenized card or an already-captured hosted-page payment.
function payBookingHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.post(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING_PAYMENTS)}`,
      async ({ params }: { params: PathParams }) => {
        console.log('[MSW] Pay booking request:', {
          subscriptionId: params.subscriptionId,
          bookingId: params.bookingId,
        });

        await delay(DELAYS.MEDIUM);

        const response = createMockBookingResponse({
          id: params.bookingId as string,
          bookingStatus: 'CONFIRMED',
          paymentStatus: 'PAID',
        });
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'bookings/:bookingId/payments',
      key: ENDPOINTS_KEYS.BOOKING_PAYMENTS,
      method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
    },
  };
}

// The customer is back from the bank: 202, no body. What it did is read from the booking afterwards.
function paymentAuthenticationNotificationHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.post(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS)}`,
      async ({ params }: { params: PathParams }) => {
        console.log('[MSW] Payment authentication notification:', {
          subscriptionId: params.subscriptionId,
          bookingId: params.bookingId,
        });

        await delay(DELAYS.SHORT);

        return new HttpResponse(null, { status: 202 });
      },
    ),
    mapping: {
      pattern: 'bookings/:bookingId/payments/authentications/notifications',
      key: ENDPOINTS_KEYS.BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS,
      method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
    },
  };
}

function passengersHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.get(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.SUBSCRIPTION_PASSENGERS)}`,
      async ({ params }: { params: PathParams }) => {
        console.log('[MSW] Passengers request:', {
          subscriptionId: params.subscriptionId,
        });

        await delay(DELAYS.SHORT);

        const response = createMockPassengersResponse();
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'passengers',
      key: ENDPOINTS_KEYS.SUBSCRIPTION_PASSENGERS,
    },
  };
}

function passengersEvaluationHandler(baseUrl: string): HandlerWithMapping {
  return {
    handler: http.post<PathParams, PassengerEvaluationPayload>(
      `${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.SUBSCRIPTION_PASSENGERS_EVALUATION)}`,
      async ({ request, params }: { request: Request; params: PathParams }) => {
        const body = await request.json() as PassengerEvaluationPayload;

        console.log('[MSW] Passengers evaluation request:', {
          subscriptionId: params.subscriptionId,
          body,
        });

        await delay(DELAYS.SHORT);

        const response = createMockPassengerEvaluationResponse(body.passengers.map(({ person_id }) => person_id));
        return HttpResponse.json(response, { status: 200 });
      },
    ),
    mapping: {
      pattern: 'passengers/evaluation',
      key: ENDPOINTS_KEYS.SUBSCRIPTION_PASSENGERS_EVALUATION,
      method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
    },
  };
}

/**
 * Create booking-related request handlers
 * @param baseUrl - BFF gateway base URL
 * @returns Array of MSW request handlers
 */
export function createBookingHandlers(baseUrl: string): HandlerWithMapping[] {
  return [
    tripSearchHandler(baseUrl),
    availabilityBrowseHandler(baseUrl),
    tripSearchParamsHandler(baseUrl),
    availabilityGroupsHandler(baseUrl),
    quotationHandler(baseUrl),
    getBookingHandler(baseUrl),
    listBookingsHandler(baseUrl),
    createBookingHandler(baseUrl),
    payBookingHandler(baseUrl),
    paymentAuthenticationNotificationHandler(baseUrl),
    passengersHandler(baseUrl),
    passengersEvaluationHandler(baseUrl),
  ];
}
